import type { ReactNode } from "react";

import type { ButtonProps } from "../Button";
import type { StrictPaddingStyle } from "../util/types";

export enum DialogSize {
  sm = 400,
  md = 600,
  lg = 800,
  xl = 1000,
}

export interface DialogProps {
  /**
   * Controls the visibility of the dialog.
   * When true, the dialog is shown; when false, it's hidden.
   */
  visible: boolean;

  /**
   * Callback function that is called when the dialog should be closed.
   * This is triggered by pressing the X button or the backdrop.
   */
  onClose: () => void;

  /**
   * The title/header text displayed at the top center of the dialog.
   * This is the main heading of the dialog.
   */
  headerText?: string;

  /**
   * Optional image to display in the center of the dialog.
   * Can be a local image (require('./image.png')) or remote URL ({uri: 'https://...'}).
   */
  image?: ReactNode;

  /**
   * Optional title text displayed in the body of the dialog.
   */
  title?: string;

  /**
   * Optional description text displayed below the label.
   * Used for longer explanatory text or instructions.
   */
  subtitle?: string;

  /**
   * Array of button props to render at the bottom of the dialog.
   */
  buttons?: ButtonProps[];

  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle;

  /**
   * Accessibility label for close Icon readers
   */
  accessibilityLabelIcon?: string;

  /**
   * Defines the maximum width of the dialog.
   * @default DialogSize.md
   */
  size?: DialogSize;

  /**
   * [web] Overrides the semantic heading level (aria-level) of the dialog
   * title for screen readers. Does not affect native platforms.
   * @default 2
   */
  headingAriaLevel?: number;

  /**
   * [web] Render dialog as a direct descendant of document.body via portal.
   * Ensures the dialog renders above all other content. Does not affect native.
   * @default false
   */
  portaled?: boolean;
}
